import React, { useEffect } from "react"
import { Flex, Form, InputNumber, Switch, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useWatch } from "antd/es/form/Form"
import { useTranslation } from "react-i18next"
import { Card } from "ui/Card"
import { Text } from "ui/Typography"
import styles from "./AvailabilityDisplay.module.css"

export const AvailabilityDisplay = () => {
  const { t } = useTranslation()
  const form = Form.useFormInstance()
  const isEnabled = useWatch("low_availability_enabled")
  // time_slots has no Form.Item (TicketedSchedule writes it via setFieldValue),
  // so the watch needs preserve — otherwise it skips the unregistered field and stays undefined
  const timeSlots = useWatch("time_slots", { form, preserve: true })
  const slotCount = timeSlots?.length ?? 0

  // Re-check the cap when the schedule shrinks after the threshold was set
  // (e.g. user deletes start times on this same tab).
  useEffect(() => {
    if (isEnabled && form.getFieldValue("low_availability_threshold") != null) {
      form.validateFields(["low_availability_threshold"]).catch(() => {})
    }
  }, [slotCount, isEnabled, form])

  return (
    <Card title={"Availability Display"} className="mb-3xl">
      <Flex vertical gap={16}>
        <Flex align="center" gap={8}>
          <Form.Item name="low_availability_enabled" valuePropName="checked" initialValue={true} noStyle>
            <Switch />
          </Form.Item>
          <Text>{"Low availability indicator"}</Text>
          <Tooltip title={"Shows a low availability warning to guests when remaining slots are at or below the threshold"}>
            <InfoCircleOutlined className={styles.infoIcon} />
          </Tooltip>
        </Flex>

        {isEnabled && (
          <Flex align="flex-start" gap={8}>
            <Text className={styles.thresholdLabel}>{"Show when remaining slots ≤"}</Text>
            <Form.Item
              name="low_availability_threshold"
              initialValue={1}
              style={{ marginBottom: 0 }}
              rules={[
                {
                  validator: (_, value) => {
                    if (value != null && slotCount > 0 && value > slotCount) {
                      return Promise.reject(t("experiences.ticketed_schedule.threshold_exceeds_slots", { count: slotCount }))
                    }
                    return Promise.resolve()
                  }
                }
              ]}
            >
              <InputNumber min={1} className={styles.thresholdInput} size="middle" />
            </Form.Item>
          </Flex>
        )}
      </Flex>
    </Card>
  )
}
